import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import { refreshQueries } from '@shared/api/hooks/refresh-queries';
import {
  getCompanyReportsActivitiesFoodListQueryKey,
  getCompanyReportsRetrieveQueryKey,
  useCompanyReportsActivitiesDestroy,
  useCompanyReportsActivitiesFoodList,
  useCompanyReportsActivitiesFoodPreviewRetrieve,
  useCompanyReportsActivitiesFoodRetrieve,
  useCompanyReportsActivitiesFoodUpdate,
} from '@shared/api/lib/company-reports/company-reports';
import {
  CompanyReportsActivitiesFoodListParams,
  CompanyReportsActivitiesFoodPreviewRetrieveParams,
  FoodActivityRetrieve,
  FoodActivityUpdate,
  PaginatedFoodActivitiesListList,
} from '@shared/api/types';
import { useQueryClient } from '@tanstack/react-query';

export const useGetFoodActivityList = (
  companyReportUuid: string,
  params?: CompanyReportsActivitiesFoodListParams
) => {
  return useCompanyReportsActivitiesFoodList<PaginatedFoodActivitiesListList>(
    companyReportUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        keepPreviousData: true,
        cacheTime: 1000 * 60 * 60 * 24,
      },
    }
  );
};

export const useGetFoodActivity = ({
  foodActivityUuid,
  reportUuid,
}: {
  foodActivityUuid: string;
  reportUuid: string;
}) => {
  return useCompanyReportsActivitiesFoodRetrieve<FoodActivityRetrieve>(
    reportUuid,
    foodActivityUuid,
    {
      request: useAxiosConfig(),
    }
  );
};

export const useUpdateFoodActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesFoodUpdate<FoodActivityUpdate>({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (data, variables) => {
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsActivitiesFoodListQueryKey(
            variables.companyReportUuid
          )
        );
      },
    },
  });
};

export const useDeleteFoodActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (data, variables) => {
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsActivitiesFoodListQueryKey(
            variables.companyReportUuid
          )
        );
      },
    },
  });
};

export const useGetFoodPreview = ({
  companyReportUuid,
  params,
  enabled = true,
}: {
  companyReportUuid: string;
  params?: CompanyReportsActivitiesFoodPreviewRetrieveParams;
  enabled?: boolean;
}) => {
  return useCompanyReportsActivitiesFoodPreviewRetrieve(
    companyReportUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        enabled: enabled,
        cacheTime: 0,
        staleTime: 0,
      },
    }
  );
};
